import { useCallback, useRef, useState } from 'react';
import { API } from '@/App';

const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';
const MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];

// Records the microphone and stops automatically after a short silence once the user has spoken.
export const useVoiceRecorder = () => {
  const [recording, setRecording] = useState(false);
  const [level, setLevel] = useState(0);
  const ref = useRef({});

  const start = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    const mimeType = MIME_TYPES.find(t => window.MediaRecorder?.isTypeSupported?.(t));
    const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Uint8Array(analyser.fftSize);
    const chunks = [];
    const startedAt = Date.now();
    let spoke = false;
    let silentSince = null;
    ref.current = { rec, cancelled: false };

    return new Promise((resolve) => {
      rec.ondataavailable = e => e.data.size && chunks.push(e.data);
      rec.onstop = () => {
        cancelAnimationFrame(ref.current.raf);
        stream.getTracks().forEach(t => t.stop());
        ctx.close().catch(() => {});
        setRecording(false);
        setLevel(0);
        resolve(ref.current.cancelled || !spoke ? null : new Blob(chunks, { type: rec.mimeType || 'audio/webm' }));
      };
      const tick = () => {
        if (rec.state !== 'recording') return;
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (const v of buf) { const x = (v - 128) / 128; sum += x * x; }
        const rms = Math.sqrt(sum / buf.length);
        setLevel(Math.min(1, rms * 6));
        const now = Date.now();
        if (rms > 0.035) { spoke = true; silentSince = null; } else if (spoke) { silentSince = silentSince || now; }
        if ((spoke && now - silentSince > 1600 && silentSince) || now - startedAt > 90000 || (!spoke && now - startedAt > 9000)) {
          rec.stop();
          return;
        }
        ref.current.raf = requestAnimationFrame(tick);
      };
      rec.start(250);
      setRecording(true);
      tick();
    });
  }, []);

  const stop = useCallback(() => { if (ref.current.rec?.state === 'recording') ref.current.rec.stop(); }, []);
  const cancel = useCallback(() => { ref.current.cancelled = true; if (ref.current.rec?.state === 'recording') ref.current.rec.stop(); }, []);

  return { recording, level, start, stop, cancel };
};

// Plays the copilot answers with the server-side voice; one shared <audio> unlocked on a user tap (iOS).
export const useSpeaker = () => {
  const [speakingId, setSpeakingId] = useState(null);
  const audioRef = useRef(null);
  const turnRef = useRef(0);

  const unlock = useCallback(() => {
    if (!audioRef.current) audioRef.current = new Audio();
    const a = audioRef.current;
    if (a.dataset.unlocked) return;
    a.src = SILENT_WAV;
    a.play().then(() => { a.dataset.unlocked = '1'; }).catch(() => {});
  }, []);

  const stop = useCallback(() => {
    turnRef.current += 1;
    const a = audioRef.current;
    if (a) { a.pause(); a.onended?.(); }
    setSpeakingId(null);
  }, []);

  const speak = useCallback(async (text, id, onStart) => {
    if (!audioRef.current) audioRef.current = new Audio();
    const a = audioRef.current;
    a.pause();
    const turn = ++turnRef.current;
    setSpeakingId(id);
    const res = await fetch(`${API}/copilot/speak`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
    if (!res.ok) {
      setSpeakingId(null);
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Lecture impossible');
    }
    const blob = await res.blob();
    if (turn !== turnRef.current) return;
    const url = URL.createObjectURL(blob);
    await new Promise((resolve) => {
      a.onended = () => { a.onended = null; resolve(); };
      a.onerror = () => resolve();
      a.src = url;
      onStart?.();
      a.play().catch(() => resolve());
    });
    URL.revokeObjectURL(url);
    setSpeakingId(cur => (cur === id ? null : cur));
  }, []);

  return { speakingId, speak, stop, unlock };
};
